import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { 
  Download, 
  Search, 
  Filter, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  Edit,
  Save,
  X,
  FileText,
  CheckCircle2,
  LogOut,
  BarChart3,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AdminAnalytics from './AdminAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const EnhancedAdminLeads = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchQuery, statusFilter, typeFilter]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/leads/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(lead => lead.lead_type === typeFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead =>
        lead.full_name.toLowerCase().includes(query) ||
        lead.email.toLowerCase().includes(query) ||
        lead.company.toLowerCase().includes(query) ||
        (lead.message && lead.message.toLowerCase().includes(query))
      );
    }

    setFilteredLeads(filtered);
  };

  const updateLeadStatus = async (leadId, newStatus, newNotes = null) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${BACKEND_URL}/api/leads/${leadId}/status?status=${newStatus}${newNotes ? `&notes=${encodeURIComponent(newNotes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lead updated successfully');
      fetchLeads();
      setSelectedLead(null);
      setEditingNotes(false);
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const exportToCSV = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/leads/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `liquicent_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Leads exported successfully');
    } catch (error) {
      console.error('Error exporting leads:', error);
      toast.error('Failed to export leads');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
      contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      qualified: 'bg-green-500/20 text-green-400 border-green-500/40',
      closed: 'bg-gray-500/20 text-gray-400 border-gray-500/40',
    };
    return colors[status] || colors.new;
  };

  const getTypeColor = (type) => {
    return type === 'demo' 
      ? 'bg-[#1B2FFF]/20 text-[#B8C6FF] border-[#1B2FFF]/40'
      : 'bg-purple-500/20 text-purple-400 border-purple-500/40';
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-[#C7CCD8]">Manage leads and view analytics</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#C7CCD8]/30 text-[#C7CCD8] hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('leads')}
            variant={activeTab === 'leads' ? 'default' : 'outline'}
            className={activeTab === 'leads' 
              ? 'bg-[#1B2FFF] text-white' 
              : 'border-[#C7CCD8]/30 text-[#C7CCD8] hover:bg-white/5'
            }
          >
            <Users className="w-4 h-4 mr-2" />
            Leads ({leads.length})
          </Button>
          <Button
            onClick={() => setActiveTab('analytics')}
            variant={activeTab === 'analytics' ? 'default' : 'outline'}
            className={activeTab === 'analytics' 
              ? 'bg-[#1B2FFF] text-white' 
              : 'border-[#C7CCD8]/30 text-[#C7CCD8] hover:bg-white/5'
            }
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'analytics' ? (
          <AdminAnalytics />
        ) : (
          <>
        {/* Filters & Export */}
        <Card className="p-6 bg-white/5 border-[#1B2FFF]/20 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#C7CCD8] w-5 h-5" />
              <Input
                placeholder="Search by name, email, company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-[#1B2FFF]/30 text-white"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white/5 border-[#1B2FFF]/30 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#1B2FFF]/30">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="bg-white/5 border-[#1B2FFF]/30 text-white">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#1B2FFF]/30">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="demo">Demo Requests</SelectItem>
                <SelectItem value="contact">Contact Sales</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-[#C7CCD8]">
              Showing {filteredLeads.length} of {leads.length} leads
            </div>
            <Button
              onClick={exportToCSV}
              variant="outline"
              className="border-[#1B2FFF]/40 text-[#B8C6FF] hover:bg-[#1B2FFF]/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export to CSV
            </Button>
          </div>
        </Card>

        {/* Leads Table */}
        {loading ? (
          <div className="text-center text-[#C7CCD8] py-20">Loading leads...</div>
        ) : filteredLeads.length === 0 ? (
          <Card className="p-12 bg-white/5 border-[#1B2FFF]/20 text-center">
            <FileText className="w-16 h-16 text-[#B8C6FF] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Leads Found</h3>
            <p className="text-[#C7CCD8]">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No leads have been captured yet'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredLeads.map((lead) => (
              <Card
                key={lead.id}
                className="p-6 bg-white/5 border-[#1B2FFF]/20 hover:border-[#1B2FFF]/50 transition-all duration-300 cursor-pointer"
                onClick={() => {
                  setSelectedLead(lead);
                  setSelectedStatus(lead.status);
                  setNotes(lead.notes || '');
                }}
              >
                <div className="grid md:grid-cols-4 gap-4">
                  {/* Lead Info */}
                  <div className="md:col-span-2">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{lead.full_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-[#C7CCD8] mb-2">
                          <Mail className="w-4 h-4" />
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-sm text-[#C7CCD8] mb-2">
                            <Phone className="w-4 h-4" />
                            {lead.phone}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm text-[#C7CCD8]">
                          <Building2 className="w-4 h-4" />
                          {lead.company}
                          {lead.company_size && ` (${lead.company_size})`}
                          {lead.role && ` • ${lead.role}`}
                        </div>
                      </div>
                    </div>
                    {lead.message && (
                      <p className="text-sm text-[#C7CCD8] italic line-clamp-2">"{lead.message}"</p>
                    )}
                  </div>

                  {/* Metadata */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getTypeColor(lead.lead_type)}>
                        {lead.lead_type === 'demo' ? 'Demo Request' : 'Contact Sales'}
                      </Badge>
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#C7CCD8]">
                      <Calendar className="w-3 h-3" />
                      {new Date(lead.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    {lead.notes && (
                      <div className="text-xs text-[#B8C6FF] flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Has notes
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLead(lead);
                        setSelectedStatus(lead.status);
                        setNotes(lead.notes || '');
                      }}
                      className="bg-[#1B2FFF] hover:bg-[#1B2FFF]/90 text-white"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Manage
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Lead Detail Modal */}
        {selectedLead && (
          <Dialog open={!!selectedLead} onOpenChange={() => {
            setSelectedLead(null);
            setEditingNotes(false);
          }}>
            <DialogContent className="max-w-2xl bg-[#050505] border-[#1B2FFF]/30 text-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white">
                  Lead Details
                </DialogTitle>
                <DialogDescription className="text-[#C7CCD8]">
                  Manage lead status and add notes
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                {/* Lead Info */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Full Name</label>
                    <p className="text-white font-semibold text-lg">{selectedLead.full_name}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Email</label>
                      <p className="text-white">{selectedLead.email}</p>
                    </div>
                    {selectedLead.phone && (
                      <div>
                        <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Phone</label>
                        <p className="text-white">{selectedLead.phone}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Company</label>
                      <p className="text-white">{selectedLead.company}</p>
                    </div>
                    {selectedLead.company_size && (
                      <div>
                        <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Company Size</label>
                        <p className="text-white">{selectedLead.company_size}</p>
                      </div>
                    )}
                  </div>

                  {selectedLead.role && (
                    <div>
                      <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Role</label>
                      <p className="text-white">{selectedLead.role}</p>
                    </div>
                  )}

                  {selectedLead.message && (
                    <div>
                      <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Message</label>
                      <p className="text-white">{selectedLead.message}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Type</label>
                      <Badge className={getTypeColor(selectedLead.lead_type)}>
                        {selectedLead.lead_type === 'demo' ? 'Demo Request' : 'Contact Sales'}
                      </Badge>
                    </div>
                    <div>
                      <label className="text-xs text-[#C7CCD8] uppercase tracking-wide">Submitted</label>
                      <p className="text-white text-sm">
                        {new Date(selectedLead.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Update */}
                <div className="pt-6 border-t border-[#1B2FFF]/20">
                  <label className="text-sm text-white font-semibold mb-3 block">Update Status</label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="bg-white/5 border-[#1B2FFF]/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#1B2FFF]/30 text-white">
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm text-white font-semibold">Notes</label>
                    {!editingNotes && (
                      <Button
                        onClick={() => setEditingNotes(true)}
                        variant="ghost"
                        className="text-[#B8C6FF] hover:text-white text-sm"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {editingNotes ? (
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this lead..."
                      rows={4}
                      className="bg-white/5 border-[#1B2FFF]/30 text-white resize-none"
                    />
                  ) : (
                    <p className="text-[#C7CCD8] bg-white/5 p-4 rounded-lg border border-[#1B2FFF]/20">
                      {selectedLead.notes || 'No notes yet'}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <Button
                    onClick={() => {
                      setSelectedLead(null);
                      setEditingNotes(false);
                    }}
                    variant="outline"
                    className="flex-1 border-[#C7CCD8]/30 text-[#C7CCD8] hover:bg-white/5"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={() => updateLeadStatus(selectedLead.id, selectedStatus, editingNotes ? notes : null)}
                    className="flex-1 bg-[#1B2FFF] hover:bg-[#1B2FFF]/90 text-white"
                    disabled={selectedStatus === selectedLead.status && (!editingNotes || notes === selectedLead.notes)}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default EnhancedAdminLeads;
